import {
  CaseBuildChecklistSummaryDto,
  CaseBuildStatus,
} from '@peritasai/types';
import {
  CatalogItemGroupEntity,
  UntrackedCatalogItemGroupEntity,
} from './catalog-item-group.entity';
import { ChecklistItemEntity } from './checklist-item.entity';

/**
 * Build Checklist Entity
 *
 * Represents the business logic and state for a case build checklist.
 * This entity wraps the DTO and provides safe access to checklist data.
 */
export class BuildChecklistEntity {
  public readonly id: string;
  public readonly buildStatus: CaseBuildStatus;
  public readonly traysTotal: number;
  public readonly traysAllocated: number;
  public readonly traysReserved: number;
  public readonly preferenceCardId: string;
  public readonly preferenceCardVersion: number;
  public readonly note?: string | null;
  public readonly hasItemDetail: boolean;

  // Internal Entity storage
  private readonly _caseCarts: CatalogItemGroupEntity[] = [];
  private readonly _trays: CatalogItemGroupEntity[] = [];
  private readonly _equipment: CatalogItemGroupEntity[] = [];
  private readonly _implants: CatalogItemGroupEntity[] = [];
  private readonly _stretchers: CatalogItemGroupEntity[] = [];
  private readonly _unexpectedItems: CatalogItemGroupEntity[] = [];
  private readonly _singlePackedInstruments: UntrackedCatalogItemGroupEntity[] =
    [];
  private readonly _untrackedInstruments: UntrackedCatalogItemGroupEntity[] =
    [];
  private readonly _supplies: UntrackedCatalogItemGroupEntity[] = [];

  constructor(dto: CaseBuildChecklistSummaryDto) {
    this.id = dto.id;
    this.buildStatus = dto.buildStatus;
    this.traysTotal = dto.traysTotal;
    this.traysAllocated = dto.traysAllocated;
    this.traysReserved = dto.traysReserved ?? 0;
    this.preferenceCardId = dto.preferenceCardId;
    this.preferenceCardVersion = dto.preferenceCardVersion;
    this.note = dto.note;
    this.hasItemDetail = dto.trackedItems !== undefined;

    // Initialize Tracked Items
    if (dto.trackedItems) {
      this._caseCarts = dto.trackedItems.caseCarts.map(
        (g) => new CatalogItemGroupEntity(g)
      );
      this._trays = dto.trackedItems.trays.map(
        (g) => new CatalogItemGroupEntity(g)
      );
      this._equipment = dto.trackedItems.equipment.map(
        (g) => new CatalogItemGroupEntity(g)
      );
      this._implants = dto.trackedItems.implants.map(
        (g) => new CatalogItemGroupEntity(g)
      );
      this._stretchers = dto.trackedItems.stretchers.map(
        (g) => new CatalogItemGroupEntity(g)
      );
      this._unexpectedItems = dto.trackedItems.unexpectedItems.map(
        (g) => new CatalogItemGroupEntity(g)
      );
    }

    // Initialize Untracked Items
    if (dto.untrackedItems) {
      this._singlePackedInstruments =
        dto.untrackedItems.singlePackedInstruments.map(
          (g) => new UntrackedCatalogItemGroupEntity(g)
        );
      this._untrackedInstruments = (dto.untrackedItems.instruments ?? []).map(
        (g) => new UntrackedCatalogItemGroupEntity(g)
      );
      this._supplies = dto.untrackedItems.supplies.map(
        (g) => new UntrackedCatalogItemGroupEntity(g)
      );
    }
  }

  /**
   * Get available carts from tracked items
   */
  public getAvailableCarts(): CatalogItemGroupEntity[] {
    return this._caseCarts;
  }

  /**
   * Check if a cart has been selected
   */
  public hasCartSelected(): boolean {
    const carts = this.getAvailableCarts();
    return carts.some((cartGroup) =>
      cartGroup.items.some((item) => item.allocated && item.assetId)
    );
  }

  /**
   * Get trays from tracked items
   */
  public getTrays(): CatalogItemGroupEntity[] {
    return this._trays;
  }

  /**
   * Get equipment from tracked items. Empty when the checklist was loaded
   * without item detail — check `hasItemDetail` before drawing conclusions
   * from an empty result.
   */
  public getEquipment(): CatalogItemGroupEntity[] {
    return this._equipment;
  }

  /**
   * Get implants from tracked items. Same `hasItemDetail` caveat as
   * {@link getEquipment}.
   */
  public getImplants(): CatalogItemGroupEntity[] {
    return this._implants;
  }

  /**
   * Get tracked items added beyond the preference card (not part of the
   * original checklist), grouped by catalog item.
   */
  public getUnexpectedItems(): CatalogItemGroupEntity[] {
    return this._unexpectedItems;
  }

  /**
   * Get single packed instruments from untracked items
   */
  public getSinglePackedInstruments(): UntrackedCatalogItemGroupEntity[] {
    return this._singlePackedInstruments;
  }

  /**
   * Get untracked instruments from untracked items
   */
  public getUntrackedInstruments(): UntrackedCatalogItemGroupEntity[] {
    return this._untrackedInstruments;
  }

  /**
   * Get supplies from untracked items
   */
  public getSupplies(): UntrackedCatalogItemGroupEntity[] {
    return this._supplies;
  }

  /**
   * Find a cart item by asset ID
   */
  public getCartItemByAssetId(
    assetId: string
  ): ChecklistItemEntity | undefined {
    const carts = this.getAvailableCarts();
    for (const cartGroup of carts) {
      const item = cartGroup.items.find((item) => item.assetId === assetId);
      if (item) {
        return item;
      }
    }
    return undefined;
  }

  /**
   * Find a cart item by checklist item ID
   */
  public getCartItemByChecklistItemId(
    checklistItemId: string
  ): ChecklistItemEntity | undefined {
    const carts = this.getAvailableCarts();
    for (const cartGroup of carts) {
      const item = cartGroup.items.find(
        (item) => item.checklistItemId === checklistItemId
      );
      if (item) {
        return item;
      }
    }
    return undefined;
  }
}
